/**
 * The reservations holding seats on this workshop (BON-478), between the
 * availability bar and the bookings roster.
 *
 * Read-only — release, resend and edit live on the bookings page's reservations
 * tab, which the Manage link points at. Shows AGE, which is what makes "this one
 * never moved" visible; time remaining alone cannot.
 *
 * Self-service checkouts are absent: their seats are in the bar's in-checkout
 * segment, but there is no emailed link and no action to take.
 */
import { Link } from 'react-router-dom';

import type { WorkshopReservation } from '../../../../api/workshops';

import { labelStyle } from './workshopFields';
import { WorkshopReservationRow } from './WorkshopReservationRow';

export interface WorkshopReservationsProps {
  /** Defaulted so version skew during a deploy cannot white-screen the page. */
  reservations?: WorkshopReservation[];
  /** `/admin/bookings` is gated on `read:bookings`; without it the Manage link would
   *  dead-end on an unauthorized page. Instructors still see the section. */
  canManage?: boolean;
  /** `roster.reservedSeats`, so this line cannot contradict the bar. */
  reservedSeats: number;
}

export function WorkshopReservations({
  reservations = [],
  reservedSeats,
  canManage = true,
}: WorkshopReservationsProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 4,
        }}
      >
        <h4 style={labelStyle}>RESERVATIONS</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {reservations.length > 0 && (
            <span style={{ fontSize: 11, color: 'var(--nb-subtle)' }}>
              {reservations.length} outstanding · {reservedSeats}{' '}
              {reservedSeats === 1 ? 'seat' : 'seats'} held
            </span>
          )}
          {canManage && (
            <Link
              to="/admin/bookings?tab=reservations"
              style={{ fontSize: 11, color: 'var(--nb-primary)', whiteSpace: 'nowrap' }}
            >
              Manage
            </Link>
          )}
        </div>
      </div>

      {reservations.length === 0 ? (
        <div
          style={{
            fontSize: 12,
            color: 'var(--nb-subtle)',
            padding: '14px 0',
            textAlign: 'center',
            borderTop: '1px solid var(--nb-border-2)',
          }}
        >
          No seats reserved yet
        </div>
      ) : (
        reservations.map((r) => <WorkshopReservationRow key={r.id} reservation={r} />)
      )}
    </div>
  );
}
